import * as R from 'ramda'
import debounce from 'lodash.debounce'
import { useSelector } from 'react-redux'
import { JoinRequest } from '@weblite/ts-action/hall'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { Action, MINUTE_MS, useStateRef } from '@weblite/logic-toolkit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BannedUser,
  Subscriber,
  Subscription,
  UserPermission,
} from '@weblite/ts-action/subscription'
import { RootState } from '$setup/redux'
import { useHall, useHallSync } from '$logic/hall/react-hook'
import { createUseEntity } from '$func/create-use-entity.react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { useSearchedUserByIds } from '$logic/users'
import { MemberPermissions } from '$logic/types/subscription'
import { useRoomAttribute } from '$logic/room/react-hook'
import { getHall } from '$logic/hall'
import {
  AddToOpenedUserList,
  DEFAULT_OPEN_LIST_TYPE,
  JOIN_HALL_REQUESTS_STORAGE_KEY,
  openedUserListRefetchAction,
  resetOpenedUserList,
  resetOpenedUserListSearch,
  useOpenedUserListAsyncIntegration,
  useOpenedUserListItems,
  useOpenedUserListItemsWithoutReset,
} from '$logic/opened-user-list'
import {
  getHallAdmins,
  getHallExceptionMembers,
  getHallRemovedSubscribers,
  getHallSubscribers,
  getJoinHallRequests,
  getSubscriptionDate,
  searchInSubscribers,
} from './other.service'
import { decodeMemberPermissions, getSubscription } from './client.service'
import { isClientsRoom } from '$logic/room'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  JOIN_HALL_REQUEST_ENABLED,
  useJoinHallRequestsBase,
} from './join-requests'
import { useIsClientUserHallAdmin } from '$comp/_app/components/chat/react-hooks'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { nothing } from '$helper/functions/utils'
import { getIsSuperUser, useIsSuperUser } from '$logic/client-user'

const getSubscriptions = (state: RootState): Record<string, Subscription> =>
  state.main.subscriptions

const getSubscriptionView = (roomId: string) => (state: RootState) =>
  getSubscriptions(state)[roomId]

const getSubscriptionWithinClientRooms = (roomId: string) => {
  if (!isClientsRoom(roomId)) return Promise.resolve<Subscription>(undefined)
  return getSubscription(roomId)
}

export const useSubscription = createUseEntity<Subscription>(
  getSubscriptionView,
  getSubscriptionWithinClientRooms,
)

export function useSubscriptions() {
  return useSelector(getSubscriptions)
}
export function useSubscriptionSync(roomId: string) {
  return useSelector(getSubscriptionView(roomId))
}

export function useSubscriptionSyncPermissions(roomId: string) {
  return useSelector(getSubscriptionView(roomId))?.permissions
}

export function useHallAdmins(hallId: string, key = DEFAULT_OPEN_LIST_TYPE) {
  const fetchFn = useCallback(() => getHallAdmins(hallId), [hallId])
  return useOpenedUserListAsyncIntegration(fetchFn, [hallId], key)
}

export function useHallAdminsCount(hallId: string, reloadAdminCount: boolean) {
  const { value, loading } = useAsyncFunction(
    () => getHallAdmins(hallId),
    [hallId, reloadAdminCount],
  )

  return { count: (value || []).length, loading }
}

export function useSearchedHallAdmins<T extends { id: string }>({
  query,
  fetchedItems,
}: {
  query: string
  fetchedItems: T[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds({
    query,
    userIds: fetchedItems.map(admin => admin.id),
  })
  return {
    loading,
    searchedItems: searchedItems.map(id =>
      fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useHallSubscribers(hallId: string) {
  const items = useOpenedUserListItems()
  const [loading, setLoading] = useState<boolean>(true)
  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(true)

  const pageTokenRef = useRef<string>('')
  const hasMoreItemToLoadRef = useRef<boolean>(true)

  const loadMore = useCallback(async () => {
    if (!hasMoreItemToLoadRef.current) return

    const res = await getHallSubscribers(hallId, pageTokenRef.current)
    setLoading(false)

    batch(() => {
      if (!res.pageToken) {
        setHasMoreItemToLoad(false)
        hasMoreItemToLoadRef.current = false
      }

      pageTokenRef.current = res.pageToken ?? ''
      AddToOpenedUserList(res.users)
    })
  }, [hallId])

  const loadMoreFnRef = useStateRef(loadMore)

  useEffect(() => {
    const resetHandler = (key: string) => {
      if (key !== DEFAULT_OPEN_LIST_TYPE) return

      setLoading(true)
      setHasMoreItemToLoad(true)

      hasMoreItemToLoadRef.current = true
      pageTokenRef.current = ''
      loadMoreFnRef.current?.()
    }

    openedUserListRefetchAction.add(resetHandler)
    return () => openedUserListRefetchAction.remove(resetHandler)
  }, [])

  return { items: items as Subscriber[], hasMoreItemToLoad, loadMore, loading }
}

const DEBOUNCE_TIME = 700
const debouncedSearchInSubscribers = debounce(
  (roomId: string, query: string, fn: (res: Subscriber[]) => unknown) =>
    searchInSubscribers(roomId, query).then(fn),
  DEBOUNCE_TIME,
)

export function useSearchedSubscribers({
  roomId,
  query,
  fetchedItems = [],
  hasMoreItemToLoad,
}: {
  roomId: string
  query: string
  fetchedItems: Subscriber[]
  hasMoreItemToLoad: boolean
}) {
  const [loading, setLoading] = useState<boolean>(false)

  const pageTokenRef = useRef<string>('')
  const queryRef = useRef<string>('')

  useEffect(() => {
    setLoading(true)
    loadMore()
      .then(() => {
        resetOpenedUserListSearch()
      })
      .catch(() => {
        setLoading(false)
      })
  }, [roomId])

  const [
    hasMoreItemToLoadInSearchHookApi,
    setHasMoreItemToLoadInSearchHookApi,
  ] = useState<boolean>(hasMoreItemToLoad)

  const hasMoreItemToLoadInSearchApiRef = useRef<boolean>(true)

  const loadMore = useCallback(async () => {
    if (!hasMoreItemToLoadInSearchApiRef.current) return

    const res = await getHallSubscribers(
      roomId,
      queryRef.current !== query ? undefined : pageTokenRef.current, // NOTE: Send pageToken while query stays same
      query,
    )
    setLoading(false)

    batch(() => {
      if (!res.pageToken) {
        setHasMoreItemToLoadInSearchHookApi(false)
        hasMoreItemToLoadInSearchApiRef.current = false
      }
      queryRef.current = query
      pageTokenRef.current = res.pageToken ?? ''
      AddToOpenedUserList(res.users)
    })
  }, [roomId, query])

  const loadMoreFnRef = useStateRef(loadMore)

  useEffect(() => {
    const resetHandler = () => {
      setHasMoreItemToLoadInSearchHookApi(true)
      hasMoreItemToLoadInSearchApiRef.current = true
      pageTokenRef.current = ''
      queryRef.current = ''
      loadMoreFnRef.current?.()
    }

    return resetHandler
  }, [query])

  const { searchedItems: localSearchResult } = useSearchedUserByIds({
    query,
    userIds: fetchedItems.map(item => item.id),
    searchThreshold: 0,
  })

  return {
    loading,
    searchedItems: localSearchResult.map(id =>
      fetchedItems.find(item => item.id === id),
    ) as Subscriber[],
    loadMore,
    hasMoreItemToLoad: hasMoreItemToLoadInSearchHookApi,
  }
}

export function useHallRemovedSubscribers(
  hallId: string,
  key = DEFAULT_OPEN_LIST_TYPE,
) {
  const { loading, items } = useOpenedUserListAsyncIntegration(
    () => getHallRemovedSubscribers(hallId),
    [hallId],
    key,
  )

  return { loading, items: items as BannedUser[] }
}

export function useHallRemovedSubscribersCount(hallId: string) {
  const { loading, value } = useAsyncFunction(
    () => getHallRemovedSubscribers(hallId),
    [hallId],
  )

  return { loading, count: value?.length }
}

export function useSearchedRemovedSubscribers({
  query,
  fetchedItems,
}: {
  query: string
  fetchedItems: BannedUser[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds({
    query,
    userIds: fetchedItems.map(item => item.id),
  })

  return {
    loading,
    searchedItems: searchedItems.map(id =>
      fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useHallNonAdminSubscribers(hallId: string) {
  const { items, ...other } = useHallSubscribers(hallId)
  return {
    ...other,
    items: (items as Subscriber[]).filter(({ admin }) => !admin),
  }
}

export function useSearchedHallNonAdminSubscribers(searchParams: {
  roomId: string
  query: string
  fetchedItems: Subscriber[]
  hasMoreItemToLoad: boolean
}) {
  const { searchedItems, ...other } = useSearchedSubscribers(searchParams)
  return {
    ...other,
    searchedItems: (searchedItems as Subscriber[]).filter(
      ({ admin }) => !admin,
    ),
  }
}

export function useGroupExceptionMembers(hallId: string) {
  const { items, loading } = useOpenedUserListAsyncIntegration(
    () => getHallExceptionMembers(hallId),
    [hallId],
  )

  return { items, loading }
}

export function useSearchedGroupExceptionMembers(searchParams: {
  hallId: string
  query: string
  fetchedItems: UserPermission[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds({
    query: searchParams.query,
    userIds: searchParams.fetchedItems.map(item => item.id),
  })

  return {
    loading,
    searchedItems: searchedItems.map(id =>
      searchParams.fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useClientUserPermissions(
  roomId: string,
): MemberPermissions | undefined {
  const { entity: hall } = useHall(roomId)
  const { entity: subscription } = useSubscription(roomId)
  const isSuperUser = useIsSuperUser()

  return useMemo(
    () => decodeMemberPermissions(hall, subscription, isSuperUser),
    [subscription?.permissions, hall?.permissions, isSuperUser],
  )
}

export function useClientUserPermissionsSync(
  roomId: string,
): MemberPermissions | undefined {
  const hall = useHallSync(roomId)
  const subscription = useSubscriptionSync(roomId)
  return useMemo(
    () => decodeMemberPermissions(hall, subscription),
    [subscription?.permissions, hall?.permissions],
  )
}

export const getClientUserPermissions = async (
  roomId: string,
): Promise<MemberPermissions | undefined> => {
  const hall = await getHall(roomId)
  const subscription = await getSubscription(roomId)
  const isSuperUser = getIsSuperUser()

  return decodeMemberPermissions(hall, subscription, isSuperUser)
}

export const getClientUserHasPermissionToForwardMessageInHall = async (
  roomId: string,
) => {
  const hall = await getHall(roomId)
  return !hall.protectContent
}

export function useClientUserHasPermissionToForwardMessage(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)
  const permissions = useSubscriptionSyncPermissions(roomId)
  const isChannel = roomType === 'CHANNEL'

  return useAsyncFunction(
    isChannel
      ? () => getClientUserHasPermissionToForwardMessageInHall(roomId)
      : () => Promise.resolve(true),
    [isChannel, permissions],
  )
}

export function useSearchInSubscribers(roomId: string, query: string) {
  const [items, setItems] = useState<Subscriber[] | undefined>([])
  const [fetchedItems, setFetchedItems] = useState<Subscriber[]>([])
  const [queryFetched, setQueryFetched] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setItems(undefined)
    if (typeof queryFetched.find(q => query.startsWith(q)) === 'string') return

    setLoading(true)
    debouncedSearchInSubscribers(roomId, query, res => {
      setItems(res)
      setFetchedItems(prevState =>
        R.uniqBy(R.prop('id'), [...prevState, ...res]),
      )

      const allFetched = res.length < 40
      if (allFetched) setQueryFetched(prevState => [...prevState, query])

      setLoading(false)
    })
  }, [roomId, query])

  const fetchedIds = useMemo(
    () => fetchedItems.map(R.prop('id')),
    [fetchedItems],
  )

  const { searchedItems: localResult } = useSearchedUserByIds({
    query,
    userIds: fetchedIds,
  })

  const result = useMemo(
    () => items || localResult.map(id => fetchedItems.find(R.propEq('id', id))),
    [items, localResult],
  )

  return { loading, items: result }
}

export function useRoomSubscriptionDate(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)
  return useAsyncFunction(
    roomType === 'CHANNEL'
      ? () => getSubscriptionDate(roomId)
      : () => Promise.resolve(undefined),
    [roomType],
  )
}

export function useJoinHallRequests({
  hallId,
  query,
  expirationInterval = 10 * MINUTE_MS,
}: {
  hallId: string
  query?: string
  expirationInterval?: number
}) {
  const isClientUserHallAdmin = useIsClientUserHallAdmin(hallId)

  const props = useMemo(() => {
    return { hallId, query, expirationInterval }
  }, [hallId, query, expirationInterval])

  const res = useConditionalHook(
    isClientUserHallAdmin && JOIN_HALL_REQUEST_ENABLED,
    {
      hook: useJoinHallRequestsBase,
      hookArgs: props,
    },
  ) || {
    hasError: false,
    loading: false,
    items: [],
    loadMore: nothing,
    hasMoreItemToLoad: false,
    totalCount: 0,
    retry: nothing,
  }

  return res
}
